import { fromJS } from 'immutable';
import React, { useCallback } from 'react';

import { convertOverlayToEditorData } from 'blocks/TextOverlayModal/utils';
import { ImmutableTextPreset } from 'redux/modules/text-presets';

import { AssetOverlayTheme } from 'types';
import useTextOverlayExtensions from '../extensions/useTextOverlayExtensions';
import useTextOverlayFont from '../hooks/useTextOverlayFonts';
import applyMigrations from '../migrations';
import { getInitialOverlay } from '../style-import-utils';
import { EditorDataV2, Size } from '../types';
import { normalizeLockedFeatures } from './lockedFeatures';
import {
  EditorAdvancedAnimationSetter,
  EditorContainerAdvancedTextConfigSetter,
  EditorContainerStyleSetter,
  EditorOverlaySetter,
  FullConfigUpdater,
  TextOverlayProviderProps,
  TextOverlayState,
  UseTextOverlay,
} from './types';
import useDynamicContent from './useDynamicContent';
import useLineBasedHeight from './useLineBasedHeight';

const TextOverlayStateContext = React.createContext<TextOverlayState | null>(
  null,
);

const TextOverlayProvider: React.FunctionComponent<TextOverlayProviderProps> = props => {
  const {
    aspectRatio,
    children,
    defaultText,
    extensions = {},
    lockedFeatures: lockedFeaturesProp,
    show,
    textOverlay,
    allowDynamicContent: allowDynamicContentProp,
  } = props;

  const lockedFeatures = React.useMemo(
    () => normalizeLockedFeatures(lockedFeaturesProp),
    [lockedFeaturesProp],
  );

  const editorHtmlRef = React.useRef<HTMLDivElement>(null);

  const [prevShow, setPrevShow] = React.useState(false);
  const [baseTextValue, setBaseTextValue] = React.useState(defaultText);
  const [draftEditorData, setDraftEditorData] = React.useState<EditorDataV2>();
  const [selectedPreset, setSelectedPreset] = React.useState<
    ImmutableTextPreset | undefined
  >();
  const [canvasSize, setCanvasSize] = React.useState<Size>({
    height: 1,
    width: 1,
  });
  const [initialEditorData, setInitialEditorData] = React.useState<
    EditorDataV2
  >(draftEditorData);

  const { getHeightForLines } = useLineBasedHeight(draftEditorData!);
  const { fonts } = useTextOverlayFont();

  const {
    allowDynamicContent,
    isDynamicContentEnabled,
    isDynamicContentAvailable,
    isDynamicContent,
    onUpdateTextType,
    onIsDynamicContentEnabledChange,
  } = useDynamicContent({
    allowDynamicContent: allowDynamicContentProp,
    initialEditorData,
    draftEditorData,
    onUpdateTextType: ({ textType, text, textHtml }) => {
      setDraftEditorData(
        draftEditorData.withMutations(s => {
          s.set('textType', textType);
          s.set('text', text);
          s.set('textHtml', textHtml);
        }),
      );

      setBaseTextValue(text);
    },
  });

  const theme: AssetOverlayTheme =
    isDynamicContentEnabled && isDynamicContent ? 'dynamic' : 'default';

  const clearPresetSelection = React.useCallback(() => {
    setSelectedPreset(undefined);
  }, []);

  const onUpdateStyle: EditorContainerStyleSetter = React.useCallback(
    (updater, keepPresetSelection = false) => {
      if (!keepPresetSelection) {
        clearPresetSelection();
      }

      setDraftEditorData(prev =>
        prev.withMutations(s =>
          s.setIn(
            ['editor', 'textStyle'],
            updater(prev.getIn(['editor', 'textStyle'])),
          ),
        ),
      );
    },
    [clearPresetSelection],
  );

  const onUpdateAdvancedTextConfig: EditorContainerAdvancedTextConfigSetter = React.useCallback(
    (updater, keepPresetSelection = false) => {
      if (!keepPresetSelection) {
        clearPresetSelection();
      }

      setDraftEditorData(prev =>
        prev.withMutations(s => {
          if (!s.get('advancedTextConfigs')) {
            s.set('advancedTextConfigs', fromJS({}));
          }

          s.set(
            'advancedTextConfigs',
            updater(prev.get('advancedTextConfigs')),
          );
        }),
      );
    },
    [clearPresetSelection],
  );

  const onUpdateOverlay: EditorOverlaySetter = React.useCallback(
    (updater, keepPresetSelection = false) => {
      if (!keepPresetSelection) {
        clearPresetSelection();
      }

      setDraftEditorData(prev => updater(prev));
    },
    [clearPresetSelection],
  );

  const handleTurnOnAdvancedAnimation: EditorAdvancedAnimationSetter = useCallback(
    (animationName, properties, options): void => {
      const {
        resetHighlight = false,
        resetOutline = false,
        defaultVisibleLines = 1,
      } = options ?? {};

      setDraftEditorData(
        draftEditorData.withMutations(s => {
          s.set(
            'advancedAnimation',
            fromJS({
              enabled: true,
              animationName,
              properties,
            }),
          );

          if (resetHighlight) {
            s.setIn(['advancedTextConfigs', 'gooeyText'], undefined);
          }

          if (resetOutline) {
            s.setIn(['editor', 'textStyle', 'textOutline'], undefined);
          }

          const newHeight = getHeightForLines(defaultVisibleLines);

          if (newHeight !== undefined) {
            s.setIn(['size', 'height'], newHeight);
          }
        }),
      );
    },
    [getHeightForLines, draftEditorData],
  );

  const handleFullUpdate: FullConfigUpdater = React.useCallback(
    (updater): void => {
      clearPresetSelection();
      setDraftEditorData(updater(draftEditorData));
    },
    [clearPresetSelection, draftEditorData],
  );

  // As the extension is used from several sub components and at separated parts
  // the extensions config is made globally available for the editor using the
  // provider.
  const {
    animationsTabExtension,
    boxSizeFitCheckerExtension,
    localPresets,
    overlayTimingExtension,
    stylePersistorExtension,
  } = useTextOverlayExtensions({
    context: {
      aspectRatio,
      baseTextValue,
      canvasSize,
      draftEditorData,
      initialEditorData,
    },
    extensions,
  });

  if (prevShow !== show) {
    setPrevShow(show);

    // Re-initializes the modal state each time it is opened
    if (show) {
      setSelectedPreset(undefined);
      setBaseTextValue(textOverlay?.get('text') || defaultText);
      const editorData = convertOverlayToEditorData(
        stylePersistorExtension.onApplyLastEditorState(
          applyMigrations(getInitialOverlay(aspectRatio, textOverlay)),
        ),
      );
      setDraftEditorData(editorData);
      setInitialEditorData(editorData);
      onIsDynamicContentEnabledChange(allowDynamicContent);
    }
  }

  const { textValue } = boxSizeFitCheckerExtension;

  return (
    <TextOverlayStateContext.Provider
      value={React.useMemo(
        () => ({
          animationsTabExtension,
          boxSizeFitCheckerExtension,
          canvasSize,
          draftEditorData,
          editorHtmlRef,
          fonts,
          localPresets,
          onFullUpdate: handleFullUpdate,
          onChangeCanvasSize: setCanvasSize,
          onChangeText: setBaseTextValue,
          onSelectPreset: setSelectedPreset,
          onTurnOnAdvancedAnimation: handleTurnOnAdvancedAnimation,
          onUpdateTextType,
          onUpdateAdvancedTextConfig,
          onUpdateOverlay,
          onUpdateStyle,
          overlayTimingExtension,
          selectedPreset,
          stylePersistorExtension,
          textValue,
          isDynamicContent,
          isDynamicContentEnabled,
          isDynamicContentAvailable,
          allowDynamicContent,
          lockedFeatures,
          theme,
        }),
        [
          animationsTabExtension,
          boxSizeFitCheckerExtension,
          canvasSize,
          draftEditorData,
          fonts,
          localPresets,
          handleFullUpdate,
          handleTurnOnAdvancedAnimation,
          onUpdateTextType,
          onUpdateAdvancedTextConfig,
          onUpdateOverlay,
          onUpdateStyle,
          overlayTimingExtension,
          selectedPreset,
          stylePersistorExtension,
          textValue,
          isDynamicContent,
          isDynamicContentEnabled,
          isDynamicContentAvailable,
          allowDynamicContent,
          lockedFeatures,
          theme,
        ],
      )}
    >
      {children}
    </TextOverlayStateContext.Provider>
  );
};

export const useTextOverlay = (): UseTextOverlay => {
  const textOverlayState = React.useContext(TextOverlayStateContext);

  if (!textOverlayState) {
    throw new Error(
      'useTextOverlay should be used within the TextOverlayProvider context',
    );
  }

  return textOverlayState;
};

export default TextOverlayProvider;
